/**
 * DeckCardGrid - Individual card in grid view with quantity badge
 */
import type { ReactElement } from 'react';
import { useCallback } from 'react';

import clsx from 'clsx';
import { FaMinus, FaPlus, FaTimes } from 'react-icons/fa';
import { IoCheckbox, IoSquareOutline } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import FlippableCardImage from '@/components/FlippableCardImage/FlippableCardImage';
import type { DeckFormat, DeckStatus, DeckZone } from '@/types/deck';
import useDeckCardOperations from '../hooks/useDeckCardOperations';
import { EnrichedDeckCard } from '../types';
import CardSelectOverlayButton from './CardSelectOverlayButton';
import DeckCardActions from './DeckCardActions';

export interface DeckGridViewCardProps {
  deckId: string;
  card: EnrichedDeckCard;
  isLocked?: boolean;
  deckFormat?: DeckFormat | null;
  deckStatus: DeckStatus;
  /** Whether this card is the current deck cover */
  isCover: boolean;
  onSetCover: (scryfallId: string) => void;
  onSetCommander?: (cardId: string, scryfallId: string) => void;
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  /** Whether multi-select mode is active */
  isSelectionMode?: boolean;
  /** Whether this card is currently selected */
  isSelected?: boolean;
  /** Toggle selection for this deck card */
  onToggleSelect?: (deckCardId: string) => void;
}

const ZONE_COLORS: Record<DeckZone, string> = {
  commander: 'border-amber-500/60',
  companion: 'border-purple-500/60',
  main: 'border-transparent',
  sideboard: 'border-cyan-500/60',
  maybeboard: 'border-zinc-500/60',
  tokens: 'border-emerald-500/60',
};

export default function DeckGridViewCard({
  deckId,
  card,
  isLocked = false,
  deckFormat,
  deckStatus,
  isCover,
  onSetCover,
  onSetCommander,
  onCardSelect,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
}: DeckGridViewCardProps): ReactElement {
  const { increase, decrease, remove, canIncrease, isLoading } = useDeckCardOperations({ deckId, isLocked, deckFormat });

  const quantity = card.quantity;
  const zone = card.zone;
  const imageUris = card.display_image_uris;

  const handleSelect = useCallback(() => {
    if (isSelectionMode) {
      onToggleSelect?.(card.id);
      return;
    }
    onCardSelect?.(card.card.id, card.id);
  }, [isSelectionMode, onToggleSelect, onCardSelect, card.card.id, card.id]);

  return (
    <div className={clsx('group relative', isLoading && 'pointer-events-none opacity-60')}>
      {/* Card Image with zone border */}
      <CardHoverPreview imageUris={imageUris} name={card.card.name} layout={card.card.layout} cardFaces={card.card.card_faces}>
        <div
          className={clsx(
            'relative overflow-hidden rounded-xl border-2 transition-transform',
            'hover:scale-[1.02] hover:shadow-lg hover:shadow-black/30',
            isSelectionMode && isSelected ? 'ring-aether border-aether/60 ring-2' : ZONE_COLORS[zone],
          )}
        >
          <FlippableCardImage
            imageUris={imageUris}
            layout={card.card.layout}
            cardFaces={card.card.card_faces}
            name={card.card.name}
            size="normal"
            className="h-auto! w-full!"
          />

          {/* Quantity Badge - Top Right */}
          {quantity > 1 && (
            <div className="pointer-events-none absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900/90 text-sm font-bold text-zinc-100 shadow-md">
              {quantity}
            </div>
          )}

          {/* Click target to open details panel or toggle selection (sits below interactive controls) */}
          {(onCardSelect || isSelectionMode) && (
            <CardSelectOverlayButton cardName={card.card.name} onSelect={handleSelect} rounded="rounded-xl" />
          )}

          {/* Selection checkbox overlay */}
          {isSelectionMode && (
            <div className="pointer-events-none absolute top-2 left-2 z-10">
              {isSelected ? (
                <IoCheckbox className="text-aether h-6 w-6 drop-shadow-md" />
              ) : (
                <IoSquareOutline className="text-pearl/70 h-6 w-6 drop-shadow-md" />
              )}
            </div>
          )}

          {/* Hover Controls Overlay */}
          {!isLocked && !isSelectionMode && (
            <div className="pointer-events-none invisible absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/80 via-black/20 to-transparent p-3 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
              {/* Card Name */}
              <p className="mb-2 truncate text-sm font-medium text-zinc-100">{card.card.name}</p>

              {/* Quantity Controls */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => decrease(card.id, quantity)}
                    className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                    aria-label="Decrease quantity"
                  >
                    <FaMinus className="h-3 w-3" />
                  </button>
                  <span className="w-8 text-center text-sm font-bold text-zinc-100">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => increase(card.id, quantity)}
                    disabled={!card.card || !canIncrease(card.card, quantity)}
                    className={clsx(
                      'pointer-events-auto flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                      card.card && canIncrease(card.card, quantity)
                        ? 'bg-zinc-800/90 text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100'
                        : 'cursor-not-allowed bg-zinc-800/50 text-zinc-500',
                    )}
                    aria-label="Increase quantity"
                  >
                    <FaPlus className="h-3 w-3" />
                  </button>
                </div>

                {/* Zone / commander / cover actions (same rules as table view) */}
                <div className="flex items-center gap-1">
                  <DeckCardActions
                    deckCard={card}
                    deckId={deckId}
                    deckFormat={deckFormat ?? null}
                    deckStatus={deckStatus}
                    isCover={isCover}
                    isLocked={isLocked}
                    variant="grid"
                    onSetCover={onSetCover}
                    onSetCommander={onSetCommander}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => remove(card.id)}
                  className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-lg bg-red-900/80 text-red-300 transition-colors hover:bg-red-800 hover:text-red-100"
                  aria-label="Remove card"
                >
                  <FaTimes className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </div>
      </CardHoverPreview>
    </div>
  );
}
